import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, 
  Users, 
  Trophy, 
  TrendingUp,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { aboutData } from '../data/mock';

const iconMap = {
  0: Target,
  1: Users,
  2: Trophy,
  3: TrendingUp,
};

const AboutSection = () => {
  return (
    <section className="section bg-[#0F0F15]" id="about">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Photo/Visual Side */}
          <div className="relative order-2 lg:order-1">
            <div className="relative">
              {/* Main card with founder visual */}
              <div className="bg-[#12121A] rounded-2xl p-8 border border-[#2A2A35]">
                {/* Founder badge */}
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FF8555] px-4 py-2 rounded-lg mb-6">
                  <Trophy size={16} className="text-black" />
                  <span className="text-sm font-semibold text-black">Founder-Led</span>
                </div>
                
                {/* Avatar placeholder */}
                <div className="w-full aspect-[4/3] rounded-xl bg-gradient-to-br from-[#1A1A24] to-[#0A0A0F] border border-[#2A2A35] mb-6 flex items-center justify-center overflow-hidden">
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center mb-4">
                      <span className="text-4xl font-bold text-black">DP</span>
                    </div>
                    <p className="text-[#6B6B78] text-sm">DP Studios</p>
                  </div>
                </div>
                
                {/* Credentials */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-white">
                    <CheckCircle2 size={18} className="text-[#FF6B35] flex-shrink-0" />
                    <span className="text-sm">Florida A&M University Business Student</span>
                  </div>
                  <div className="flex items-center gap-3 text-white">
                    <CheckCircle2 size={18} className="text-[#FF6B35] flex-shrink-0" />
                    <span className="text-sm">Data Analytics & Sports Media Background</span>
                  </div>
                  <div className="flex items-center gap-3 text-white">
                    <CheckCircle2 size={18} className="text-[#FF6B35] flex-shrink-0" />
                    <span className="text-sm">NIL Branding & Digital Marketing Experience</span>
                  </div>
                </div>
              </div>
              
              {/* Floating badge - Experience */}
              <div className="absolute -right-4 top-1/4 bg-[#12121A] border border-[#FF6B35]/30 text-white rounded-xl p-4 shadow-lg animate-float glow-orange">
                <div className="text-2xl font-bold">50+</div>
                <div className="text-xs text-[#6B6B78]">Projects Delivered</div>
              </div>
              
              {/* Floating badge - Focus */}
              <div 
                className="absolute -left-4 bottom-1/4 bg-[#12121A] border border-[#00D4FF]/30 rounded-xl p-4 shadow-lg glow-cyan"
                style={{ animation: 'float 4s ease-in-out infinite 1s' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00D4FF]/20 to-[#00D4FF]/10 flex items-center justify-center">
                    <TrendingUp size={16} className="text-[#00D4FF]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Conversion</div>
                    <div className="text-xs text-[#6B6B78]">Focused</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right - Content Side */}
          <div className="order-1 lg:order-2">
            <span className="inline-block text-sm font-semibold text-[#FF6B35] uppercase tracking-wider mb-4">
              About Me
            </span>
            <h2 className="heading-1 mb-2">
              {aboutData.headline}
            </h2>
            <p className="text-[#FF6B35] font-medium text-lg mb-6">
              {aboutData.founder}
            </p>
            
            <p className="body-large mb-6">
              {aboutData.intro}
            </p>
            
            {/* Experience points */}
            <div className="space-y-3 mb-8">
              {aboutData.experience.map((exp, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={14} className="text-black" />
                  </div>
                  <span className="text-white">{exp}</span>
                </div>
              ))}
            </div>
            
            <p className="body-medium text-[#6B6B78] italic mb-8 pl-4 border-l-4 border-[#FF6B35]">
              "{aboutData.philosophy}"
            </p>
            
            {/* Approach cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {aboutData.approach.map((item, index) => {
                const IconComponent = iconMap[index];
                return (
                  <div 
                    key={index}
                    className="bg-[#12121A] rounded-xl p-4 border border-[#2A2A35] hover:border-[#FF6B35]/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-[#FF6B35]/10 flex items-center justify-center mb-3">
                      <IconComponent size={20} className="text-[#FF6B35]" />
                    </div>
                    <h4 className="font-semibold text-white text-sm mb-1">
                      {item.title}
                    </h4>
                    <p className="text-xs text-[#6B6B78]">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
            
            <Link 
              to="/contact"
              className="btn-primary"
            >
              {aboutData.cta}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
